/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from 'react';

import type { AgentExecution } from '@/sections/insights/aiAssistant/types/conversation.types';

interface UseAgentExecutionProps {
  messages: any[];
  activeAgent: string | null;
  conversationId: string | null;
  agents: Array<{ name: string; title?: string }> | undefined;
}

export function useAgentExecution({
  messages,
  activeAgent,
  conversationId,
  agents,
}: UseAgentExecutionProps) {
  const [agentExecutions, setAgentExecutions] = useState<
    Map<string, AgentExecution>
  >(new Map());
  const [expandedAgentCards, setExpandedAgentCards] = useState<Set<string>>(
    new Set()
  );
  const [loadingStatus, setLoadingStatus] = useState<string>('');
  const [thinkingStartTime, setThinkingStartTime] = useState<number | null>(
    null
  );

  const toggleAgentCard = (id: string) => {
    setExpandedAgentCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Track agent execution when response arrives
  useEffect(() => {
    if (conversationId && activeAgent && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const isThinking =
        lastMessage &&
        lastMessage.role !== 'chariot' &&
        lastMessage.role !== 'planner-output';

      // Get agent title from agent list
      const agentData = agents?.find(
        (a: { name: string }) => a.name === activeAgent
      );
      const agentTitle = agentData?.title || activeAgent;

      // Count tool calls
      const toolCalls = messages.filter(msg => msg.role === 'tool call');

      const execution: AgentExecution = {
        agentName: activeAgent,
        agentTitle: agentTitle,
        status: isThinking ? 'running' : 'completed',
        toolCount: toolCalls.length,
        tokenCount: 0, // TODO: Get actual token count from backend
        toolCalls: toolCalls.map((tc: any) => {
          try {
            const toolData = JSON.parse(tc.toolUseContent || tc.content);
            return {
              name: toolData?.Name || toolData?.tool || 'tool',
              status: 'done' as const,
            };
          } catch {
            return { name: 'tool', status: 'done' as const };
          }
        }),
      };

      setAgentExecutions(prev => new Map(prev).set(conversationId, execution));
    }
  }, [messages, activeAgent, conversationId, agents]);

  // Update loading status based on actual backend roles and activity
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const isThinking =
      lastMessage &&
      lastMessage.role !== 'chariot' &&
      lastMessage.role !== 'planner-output';

    if (!isThinking) {
      setLoadingStatus('');
      setThinkingStartTime(null);
      return;
    }

    // Start timing when thinking begins
    if (isThinking && !thinkingStartTime) {
      setThinkingStartTime(Date.now());
    }

    const recentMessages = messages.slice(-10);
    const lastToolCall = recentMessages
      .filter(msg => msg.role === 'tool call')
      .pop();
    const lastToolResponse = recentMessages
      .filter(msg => msg.role === 'tool response')
      .pop();

    const isExecuting =
      lastToolCall &&
      (!lastToolResponse ||
        new Date(lastToolCall.timestamp || '').getTime() >
          new Date(lastToolResponse.timestamp || '').getTime());

    if (isExecuting && lastToolCall) {
      try {
        const toolData = JSON.parse(
          (lastToolCall as any).toolUseContent || lastToolCall.content
        );
        const toolName = toolData?.Name || toolData?.tool || 'tool';

        // Provide more specific status based on tool type
        if (toolName === 'query') {
          const input = toolData?.Input || toolData?.input;
          if (input?.node?.labels) {
            setLoadingStatus(`Querying ${input.node.labels.join(', ')}...`);
            return;
          }
          setLoadingStatus('Querying database...');
          return;
        }

        if (toolName === 'job') {
          const input = toolData?.Input || toolData?.input;
          const capability = input?.capability || 'scan';
          setLoadingStatus(`Running ${capability}...`);
          return;
        }

        if (toolName === 'schema') {
          setLoadingStatus('Fetching schema information...');
          return;
        }

        setLoadingStatus(`Executing ${toolName}...`);
      } catch {
        setLoadingStatus('Executing tool...');
      }
    } else if (lastToolResponse) {
      // Check if we have results to analyze
      try {
        const responseData = JSON.parse(lastToolResponse.content);
        if (responseData.count !== undefined && responseData.count > 0) {
          setLoadingStatus(`Analyzing ${responseData.count} results...`);
          return;
        }
        if (responseData.error) {
          setLoadingStatus('Handling error...');
          return;
        }
      } catch {
        // Not JSON
      }
      setLoadingStatus('Analyzing results...');
    } else {
      setLoadingStatus('Thinking...');
    }
  }, [messages, thinkingStartTime]);

  return {
    agentExecutions,
    expandedAgentCards,
    loadingStatus,
    thinkingStartTime,
    toggleAgentCard,
  };
}
